import React, { ReactNode } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Globe, MoveUpRight, MoveDownRight, MoveRight } from 'lucide-react';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import { explainerSlugKey } from 'src/shared/constants/portfolio';
import { PortfolioData } from 'src/app/portfolio/state/api/portfolioQuery';
import usePortfolioAnalytics from 'src/app/portfolio/views/portfolio-summary/hooks/usePortfolioAnalytics';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { SortOrder } from 'src/shared/types';
import { HabitatInsightsMetricType } from 'src/shared/types/graph/survey';
import PortfolioExploreButton from './PortfolioExploreButton';
import SummaryCard from './SummaryCard';

const landCoverChartColors: { label: string; color: string; metricKey?: string }[] = [
    { label: 'Water', color: '#6B98E1' },
    { label: 'Trees', color: '#4C7F47' },
    { label: 'Grass', color: '#8EB24E' },
    { label: 'Flooded vegetation', metricKey: 'Flooded Vegetation', color: '#8385C8' },
    { label: 'Crops', color: '#D19833' },
    { label: 'Shrub and scrub', metricKey: 'Shrub and Scrub', color: '#D4C556' },
    { label: 'Built', color: '#AD2A21' },
    { label: 'Bare', color: '#A29B8F' },
    { label: 'Snow and ice', metricKey: 'Snow and Ice', color: '#B39DE3' },
    { label: 'Cumulative vegetation area', color: '#4C7F47', metricKey: 'Cumulative Vegetation Area' },
];

const LandCoverChart = ({ portfolioData }: { portfolioData: PortfolioData }) => {
    const landCover = portfolioData.summary.metrics.find(metric => metric.label === 'Land Cover');
    const navigate = useAppNavigation();
    const { currentCustomerId } = useCustomer();

    const trackAnalytics = usePortfolioAnalytics();

    if (!landCover || !landCover.metricWithTrend) {
        return null;
    }

    const years = landCover?.metricWithTrend ? Object.keys(landCover.metricWithTrend).map(Number).sort() : [];

    const initialYear = years[0];
    const initialMetrics = landCover.metricWithTrend[initialYear];

    const latestYear = years[years.length - 1];
    const latestMetrics = landCover.metricWithTrend[latestYear];

    const calculateTrend = (metricKey: string) => {
        const initialValue = initialMetrics[metricKey] || 0;
        const latestValue = latestMetrics[metricKey] || 0;
        return ((latestValue - initialValue) / (initialValue || 1)) * 100;
    };

    const vegetationAreaTrend = calculateTrend('Cumulative Vegetation Area');
    const treesTrend = calculateTrend('Trees');

    const pieChartData = landCoverChartColors
        .filter(entry => entry.label !== 'Cumulative vegetation area')
        .map(entry => ({
            label: entry.label,
            value: latestMetrics[entry.metricKey || entry.label] || (0 as number),
            color: entry.color,
            initialYear: initialYear,
            latestYear: latestYear,
            trendValue: calculateTrend(entry.metricKey || entry.label),
        }));

    const handleExploreClick = () => {
        navigate.toPortfolioSiteExplorer({
            customerId: currentCustomerId,
            selectedMetric: HabitatInsightsMetricType.LAND_COVER,
            sortDirection: SortOrder.ASC,
            view: 'list',
        });
    };

    return (
        <SummaryCard
            dataTestId='portfolio-land-cover-chart'
            containerClasses='gap-3 p-5'
            cardIcon={<Globe className='text-primary' />}
            cardTitle='Land cover'
            cardSubtitle='Distribution of land cover types across the site, highlighting dominant land cover and land uses.'
            titleExplainerSlug={{
                defaultSlug: explainerSlugKey.portfolioLandCover,
                explainerSlug: `${explainerSlugKey.portfolioLandCover}-te`,
            }}
            cardBody={
                <div className='flex gap-5'>
                    <div className='flex gap-6'>
                        <CountBlock
                            count={`${latestMetrics['Cumulative Vegetation Area'] || 0}%`}
                            text={
                                <div className='flex gap-2 items-center'>
                                    Combined vegetated area
                                    <div className='mt-[-5px]'>
                                        <QuestionButtonHelp
                                            type='api'
                                            placement='right'
                                            slug={explainerSlugKey.portfolioCombinedVegetatedArea}
                                            detailedExplainerSlug={`${explainerSlugKey.portfolioCombinedVegetatedArea}-te`}
                                            onTooltipHover={() =>
                                                trackAnalytics.trackExplainerHover({
                                                    slug: explainerSlugKey.portfolioCombinedVegetatedArea,
                                                })
                                            }
                                            onDetailedHelpClick={() =>
                                                trackAnalytics.trackDetailedExplainerClick({
                                                    slug: `${explainerSlugKey.portfolioCombinedVegetatedArea}-te`,
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                            }
                            initialYear={initialYear}
                            trendValue={vegetationAreaTrend}
                        />

                        <CountBlock
                            count={`${latestMetrics.Trees || 0}%`}
                            text='Tree Cover'
                            initialYear={initialYear}
                            trendValue={treesTrend}
                        />
                    </div>

                    <div className='bg-grey-20 w-[1px]'>&nbsp;</div>

                    <div className='flex gap-8'>
                        <div className='relative h-[140px] w-[140px]'>
                            <div className='absolute inset-0 flex items-center justify-center text-xl text-grey-80'>{latestYear}</div>
                            <ResponsiveContainer width='100%' height='100%'>
                                <PieChart>
                                    <Pie
                                        dataKey='value'
                                        data={pieChartData}
                                        cx='50%'
                                        cy='50%'
                                        innerRadius={45}
                                        outerRadius={65}
                                        fill='#8884d8'
                                    >
                                        {pieChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>

                                    <Tooltip content={<CustomTooltip />} allowEscapeViewBox={{ x: true, y: true }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>

                        <div className='flex gap-5'>
                            <div className='flex flex-col gap-2'>
                                {pieChartData.slice(0, 5).map((entry, index) => (
                                    <div key={index} className='flex gap-2 items-center'>
                                        <span className='rounded-full h-4 w-4' style={{ backgroundColor: entry.color }}></span>
                                        <div className='flex gap-3'>
                                            <span className='text-sm text-grey-80 w-[140px]'>{entry.label}</span>
                                            <span className='text-sm text-black'>{entry.value}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className='flex flex-col gap-2 place-self-start'>
                                {pieChartData.slice(5).map((entry, index) => (
                                    <div key={index} className='flex gap-2 items-center'>
                                        <span className='rounded-full h-4 w-4' style={{ backgroundColor: entry.color }}></span>
                                        <div className='flex gap-3'>
                                            <span className='text-sm text-grey-80 w-[140px]'>{entry.label}</span>
                                            <span className='text-sm text-black'>{entry.value}%</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            }
            action={<PortfolioExploreButton sectionName='land-cover' onClick={handleExploreClick} />}
            frameworks={landCover.frameworks}
        />
    );
};

const getColorInfo = (trendValue: number) => {
    let colorClass = 'text-red-100';
    let arrowIcon = <MoveDownRight className='text-red-100 h-4 w-4' />;
    if (trendValue >= -0.1 && trendValue <= 0.1) {
        colorClass = 'text-orange-100';
        arrowIcon = <MoveRight className='text-orange-100 h-4 w-4' />;
    } else if (trendValue > 0.1) {
        colorClass = 'text-green-100';
        arrowIcon = <MoveUpRight className='text-green-100 h-4 w-4' />;
    }
    return {
        colorClass: colorClass,
        arrowIcon: arrowIcon,
    };
};

type CountBlockProps = {
    count: number | string;
    text: ReactNode;
    initialYear: number;
    trendValue: number;
};

const CountBlock = ({ count, text, initialYear, trendValue }: CountBlockProps) => {
    const colorInfo = getColorInfo(trendValue);

    return (
        <div className='flex flex-col gap-3'>
            <div className='flex flex-col'>
                <span className='text-[42px] text-grey-80'>{count}</span>
                <span className='flex items-center text-sm text-grey-80 pl-1'>{text}</span>
            </div>
            <div className='flex gap-2 bg-grey-0 px-2 py-1 rounded-2xl text-xs items-center w-fit'>
                {colorInfo.arrowIcon}
                <div className='text-xs'>
                    <span className={colorInfo.colorClass}>{trendValue.toFixed(2)}%</span>
                    <span className='text-grey-80'> since {initialYear}</span>
                </div>
            </div>
        </div>
    );
};

// Used type any as it is expected by Recharts Tooltip
const CustomTooltip = ({ active, payload }: any) => {
    const isVisible = active && payload && payload.length;
    if (!isVisible) {
        return null;
    }

    const payloadMetadata = payload[0].payload;
    const colorInfo = getColorInfo(payloadMetadata.trendValue);

    return (
        <div className='flex-1 flex self-stretch flex-col gap-3 bg-white p-4 rounded-lg shadow-portfolio-summary-card min-w-[240px]'>
            <div className='flex gap-2 items-center'>
                <span className='rounded-full h-4 w-4' style={{ backgroundColor: payload[0].payload.color }}></span>
                <span className='text-sm text-black'>{payloadMetadata.label}</span>
            </div>
            <div className='flex flex-col gap-2'>
                <div className='flex gap-2 justify-between'>
                    <span className='text-sm text-grey-80'>{payloadMetadata.latestYear} Land cover:</span>
                    <span className='flex text-xs text-black'>{payloadMetadata.value}%</span>
                </div>
                <div className='flex gap-2 justify-between'>
                    <span className='text-sm text-grey-80'>Trend since {payloadMetadata.initialYear}:</span>
                    <span className={`flex text-xs items-center gap-1 ${colorInfo.colorClass}`}>
                        {colorInfo.arrowIcon}
                        {payloadMetadata.trendValue.toFixed(2)}%
                    </span>
                </div>
            </div>
        </div>
    );
};

export default LandCoverChart;
